import equal from "deep-equal";
import { ComponentProps, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { SwiperSlide } from "swiper/react";

import BasicCard from "@components/cards/BasicCard";
import styles from "@components/drive/Drive.module.css";
import WorksheetFilters from "@components/forms/WorksheetFilters";
import Slides from "@components/Slides";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import InternalLink from "@components/typography/InternalLink";
import useMyClient from "@hooks/useMyClient";
import { JourneyStatusEnum } from "@typing/Enums";
import { GetJourneyWithDocumentsQuery, GoalStatusEnum } from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

type Filters = ComponentProps<typeof WorksheetFilters>["filters"];

type Journey = GetJourneyWithDocumentsQuery["journey"];

type Props = {
  journey: Journey;
};

type WorksheetProps = {
  journeyId: string;
  worksheet: Journey["worksheets"][0];
};

const Worksheet = ({ journeyId, worksheet }: WorksheetProps) => {
  const myClient = useMyClient();

  return (
    <InternalLink
      stylized={false}
      to={journeyRoute({
        journeyId,
        linkAfterJourney: `/worksheets/${worksheet.id}`,
        myClient
      })}
    >
      <BasicCard boxShadow>
        <div className={styles.worksheet}>
          <div>
            <BodyCopy color="gray-500">
              <FormattedMessage id="pages.drive.worksheet" />
            </BodyCopy>
          </div>
          <BodyCopy bold color="gray-500">
            {worksheet.title}
          </BodyCopy>
        </div>
      </BasicCard>
    </InternalLink>
  );
};

/* eslint-disable @typescript-eslint/naming-convention */

const breakPoints = {
  1: {
    slidesPerView: 1.3
  },
  576: {
    slidesPerView: 2.4
  },
  767: {
    slidesPerView: 5
  }
};
/* eslint-enable @typescript-eslint/naming-convention */

const Worksheets = ({ journey }: Props) => {
  const [filters, setFilters] = useState<Filters>({
    status: null
  });

  const baseWorksheets = journey.worksheets.filter(
    worksheet => !worksheet.goal || worksheet.goal.status === GoalStatusEnum.ACTIVE
  );

  const worksheets = useMemo(() => {
    if (filters.status === JourneyStatusEnum.IN_PROGRESS) {
      return baseWorksheets.filter(worksheet => !worksheet.completed);
    }
    if (filters.status === JourneyStatusEnum.COMPLETED) {
      return baseWorksheets.filter(worksheet => worksheet.completed);
    }

    return baseWorksheets;
  }, [filters.status, baseWorksheets]);

  const handleFilterChange = (newFilters: Filters) => {
    if (!equal(newFilters, filters)) {
      setFilters(newFilters);
    }
  };

  if (baseWorksheets.length === 0) {
    return <div className={styles.worksheetSection}></div>;
  }

  return (
    <div className={styles.worksheetSection} id="worksheets">
      <Heading2>
        <FormattedMessage id="pages.drive.worksheets" />
      </Heading2>

      <div className={styles.worksheetFilters}>
        <WorksheetFilters filters={filters} onChange={handleFilterChange} />
      </div>

      <div className={styles.showMobile + " " + styles.recentSlides}>
        <Slides breakpoints={breakPoints} hideNavigation={true} spaceBetween={21}>
          {worksheets.map(worksheet => (
            <SwiperSlide key={worksheet.id}>
              <Worksheet journeyId={journey.id} worksheet={worksheet} />
            </SwiperSlide>
          ))}
        </Slides>
      </div>

      <div className={styles.worksheets}>
        {worksheets.map(worksheet => (
          <div key={worksheet.id}>
            <Worksheet journeyId={journey.id} worksheet={worksheet} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Worksheets;
